<app-season-applications-filter
    [applicationFilterStates]="applicationFilterStates"
    [payoutPeriodOptions]="payoutPeriodOptions()"
    [payoutPeriodOptionsLoading]="payoutPeriodOptionsLoading()"
    (filtersChange)="applicationFilters.set($event)"
/>
@if (applicationsSignal()?.length || isLoadingApplications()) {
    <div class="relative mb-4">
        <div
            class="overflow-x-auto 2xl:overflow-x-visible w-full max-h-[60vh] min-h-[150px] transition-all duration-200"
            [class.blur-sm]="isLoadingApplications()"
            #table
        >
            <sh-table class="block min-w-[1500px] 2xl:min-w-full w-full">
                <sh-table-row slot="header" labelRows="2">
                    @for (column of columns; track $index) {
                        <sh-table-head unsortable [columns]="column.width">
                            <span class="text-xs">{{ column.name | translate }}</span>
                        </sh-table-head>
                    }
                </sh-table-row>
                @for (application of applicationsSignal(); track application.id) {
                    <sh-table-row>
                        <!-- GID -->
                        <sh-table-cell [columns]="columns[0].width">
                            <span
                                [id]="'application-' + application.id + '-gid'"
                                class="block min-w-0 w-full truncate whitespace-nowrap"
                            >
                                {{ application.employeeGid }}
                            </span>
                            <sh-tooltip
                                [target]="'application-' + application.id + '-gid'"
                                [label]="application.employeeGid"
                                placement="bottom"
                            />
                        </sh-table-cell>
                        <!-- Name -->
                        <sh-table-cell [columns]="columns[1].width">
                            @if (application?.employee) {
                                <span
                                    [id]="'application-' + application.id + '-name'"
                                    (click)="openApplication(application)"
                                    class="block min-w-0 w-full cursor-pointer truncate whitespace-nowrap text-[var(--logo-1)]"
                                >
                                    {{ application.employee.firstName }} {{ application.employee.lastName }}
                                </span>
                                <sh-tooltip
                                    [target]="'application-' + application.id + '-name'"
                                    [label]="application.employee.firstName + ' ' + application.employee.lastName"
                                    placement="bottom"
                                />
                            } @else {
                                <div>No name provided</div>
                            }
                        </sh-table-cell>
                        <!-- Application Status -->
                        <sh-table-cell
                            [columns]="columns[2].width"
                            class="[--sh-table-cell-wrapper-margin:0.25rem_0.75rem] [--sh-table-cell-line-height:1.5rem] flex items-center justify-center self-center"
                        >
                            <app-application-status-badge
                                [status]="application.status"
                                translationKeyPrefix="seasons.season-details.application-status."
                                class="inline-flex items-center"
                            />
                            @if (application.statusReason; as statusReason) {
                                <span
                                    [id]="'application-' + application.id + '-status-reason'"
                                    class="icon-circle-information align-middle ml-2"
                                ></span>
                                <sh-tooltip
                                    [target]="'application-' + application.id + '-status-reason'"
                                    [label]="
                                        'seasons.season-overview.table.' + (statusReason | kebabCase) + '-tooltip'
                                            | translate
                                    "
                                    placement="bottom"
                                />
                            }
                        </sh-table-cell>
                        <!-- Monthly Salary Range -->
                        <sh-table-cell [columns]="columns[3].width">
                            <span
                                [id]="'application-' + application.id + '-salary'"
                                class="block min-w-0 w-full truncate whitespace-nowrap"
                            >
                                {{ application.salaryRange?.lowerBound }} - {{ application.salaryRange?.upperBound }} €
                            </span>
                            <sh-tooltip
                                [target]="'application-' + application.id + '-salary'"
                                [label]="
                                    (application.salaryRange?.lowerBound ?? null) +
                                    ' - ' +
                                    (application.salaryRange?.upperBound ?? null) +
                                    ' €'
                                "
                                placement="bottom"
                            />
                        </sh-table-cell>
                        <!-- Application Duration Type -->
                        <sh-table-cell [columns]="columns[4].width">
                            <span
                                [id]="'application-' + application.id + '-type'"
                                class="block min-w-0 w-full truncate whitespace-nowrap"
                            >
                                {{ application.type | translate }}
                            </span>
                            <sh-tooltip
                                [target]="'application-' + application.id + '-type'"
                                [label]="application.type | translate"
                                placement="bottom"
                            />
                        </sh-table-cell>
                        <!-- Fiscal Half-Year -->
                        <sh-table-cell [columns]="columns[5].width">
                            <div
                                [id]="'application-' + application.id + '-fiscal-half-year'"
                                class="block min-w-0 w-full truncate whitespace-nowrap text-center"
                            >
                                {{ application.fiscalHalfYear.key | translate: application.fiscalHalfYear.params }}
                            </div>
                            <sh-tooltip
                                [target]="'application-' + application.id + '-fiscal-half-year'"
                                [label]="application.fiscalHalfYear.key | translate: application.fiscalHalfYear.params"
                                placement="bottom"
                            />
                        </sh-table-cell>
                        <!-- Children -->
                        <sh-table-cell [columns]="columns[6].width">
                            <div class="flex justify-center">
                                {{ application.children?.length || 0 }}
                            </div>
                        </sh-table-cell>
                        <!-- Spouse -->
                        <sh-table-cell
                            [columns]="columns[7].width"
                            class="[--sh-table-cell-wrapper-margin:0.25rem_0.75rem] [--sh-table-cell-line-height:1.5rem] flex items-center justify-center self-center"
                        >
                            <div class="flex h-full items-center justify-center">
                                @if (application.spouse) {
                                    <sh-icon icon="checkmark" color="green" size="xs" />
                                } @else {
                                    <sh-icon icon="cancel" color="red" size="xs" />
                                }
                            </div>
                        </sh-table-cell>
                        <!-- Last Year Payout -->
                        <sh-table-cell [columns]="columns[8].width">
                            <span
                                [id]="'application-' + application.id + '-last-year-payout'"
                                class="block min-w-0 w-full truncate whitespace-nowrap"
                            >
                                {{ application.lastYearPayout | customCurrency }}
                            </span>
                            <sh-tooltip
                                [target]="'application-' + application.id + '-last-year-payout'"
                                [label]="(application.lastYearPayout | customCurrency) ?? ''"
                                placement="bottom"
                            />
                        </sh-table-cell>
                        <!-- Valuation Points -->
                        <sh-table-cell [columns]="columns[9].width">
                            <span
                                [id]="'application-' + application.id + '-evaluation-points'"
                                class="block min-w-0 w-full truncate whitespace-nowrap"
                            >
                                {{ application.evaluationPoints }}
                            </span>
                            <sh-tooltip
                                [target]="'application-' + application.id + '-evaluation-points'"
                                [label]="application.evaluationPoints?.toString() ?? ''"
                                placement="bottom"
                            />
                        </sh-table-cell>
                        <!-- Potential Payout -->
                        <sh-table-cell [columns]="columns[10].width">
                            <span
                                [id]="'application-' + application.id + '-potential-payout'"
                                class="block min-w-0 w-full truncate whitespace-nowrap"
                            >
                                {{ application.potentialPayout | customCurrency }}
                            </span>
                            <sh-tooltip
                                [target]="'application-' + application.id + '-potential-payout'"
                                [label]="(application.potentialPayout | customCurrency) ?? ''"
                                placement="bottom"
                            />
                        </sh-table-cell>
                        <!-- Comment -->
                        <sh-table-cell
                            [columns]="columns[11].width"
                            class="[--sh-table-cell-wrapper-margin:0.25rem_0.75rem] [--sh-table-cell-line-height:1.5rem] flex items-center justify-center gap-3 self-center"
                        >
                            <div class="flex h-full items-center justify-center gap-3">
                                <sh-icon
                                    [id]="'application-' + application.id + '-comment'"
                                    icon="comment"
                                    size="xs"
                                    class="cursor-pointer block"
                                    [color]="application.comment ? 'var(--logo-1)' : 'var(--text-secondary)'"
                                />
                                <sh-tooltip
                                    [label]="application.comment ?? ''"
                                    [target]="'application-' + application.id + '-comment'"
                                    placement="bottom"
                                />
                                @if (season(); as season) {
                                    <sh-icon
                                        icon="edit"
                                        button
                                        size="xs"
                                        [color]="
                                            season.status !== seasonStatus.CLOSED
                                                ? 'var(--logo-1)'
                                                : 'var(--text-secondary)'
                                        "
                                        [disabled]="season.status === seasonStatus.CLOSED"
                                        (click)="
                                            season.status !== seasonStatus.CLOSED && openCommentDialog(application)
                                        "
                                    />
                                }
                            </div>
                        </sh-table-cell>
                        <!-- Selected for Payout -->
                        <sh-table-cell
                            [columns]="columns[12].width"
                            class="[--sh-table-cell-wrapper-margin:0.25rem_0.75rem] [--sh-table-cell-line-height:1.5rem] flex items-center justify-center self-center"
                        >
                            <div class="flex h-full items-center justify-center">
                                @if (season(); as season) {
                                    <sh-checkbox
                                        [disabled]="
                                            season.status === seasonStatus.CLOSED ||
                                            application.status !== applicationStatus.SUBMITTED ||
                                            (!application.isSelectedForPayout &&
                                                applicationPotentialPayoutsSignal()[$index] >
                                                    availableSeasonBudgetSignal())
                                        "
                                        [active]="application.isSelectedForPayout"
                                        (checkedChange)="toggleSelectedForPayout(application)"
                                    />
                                }
                            </div>
                        </sh-table-cell>
                    </sh-table-row>
                }
            </sh-table>
        </div>
        @if (isLoadingApplications()) {
            <sh-spinner size="m" overlay />
        }
    </div>
    <sh-pagination
        class="season-applications-pagination"
        [pages]="totalPages()"
        [currentPage]="pageIndex() + 1"
        (clickedPageMove)="onPaginationMove($event)"
        (nextPageMove)="onPaginationMove($event)"
        (previousPageMove)="onPaginationMove($event)"
    />
} @else {
    <p class="flex items-center justify-center text-lg pt-10 pb-10">
        {{
            'seasons.season-details.table.' +
                (applicationFilters() ? 'no-application-results' : 'no-applications-message') | translate
        }}
    </p>
}
